"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Info as HelpCircle, ArrowRight, MessageSquare } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  question: "What is an SEO audit?",
  answer: "An SEO audit is a comprehensive diagnostic health check of your website. It evaluates technical infrastructure, crawlability, indexation, on-page content relevance, user experience signals, and link profile to identify existing blockers and clear opportunities for search growth."
}, {
  id: "item-2",
  question: "What does an SEO audit include?",
  answer: "A complete audit covers deep technical crawling, indexation diagnostics, site architecture, Core Web Vitals performance, structured data schema, on-page copy and metadata, search intent alignment, backlink profile assessment, and a prioritized action roadmap."
}, {
  id: "item-3",
  question: "Why does my website need an SEO audit?",
  answer: "Search engine algorithms, platform code, and competitor landscapes evolve continually. An audit uncovers hidden technical issues, indexing errors, keyword cannibalisation, or outdated optimisation practices that silently restrict your organic visibility and conversions."
}, {
  id: "item-4",
  question: "How long does an SEO audit take?",
  answer: "The duration depends on the overall size of your website, technical architecture complexity, and required audit scope. I agree on a clear delivery timeframe upfront following an initial review of your website."
}, {
  id: "item-5",
  question: "How often should an SEO audit be carried out?",
  answer: "Most websites benefit from a full comprehensive audit once a year, or immediately before and after significant events such as website redesigns, CMS migrations, major platform updates, or unexpected organic traffic fluctuations."
}, {
  id: "item-6",
  question: "What is the difference between an SEO audit and a Technical SEO audit?",
  answer: <span>
        A full SEO audit evaluates the entire spectrum of search performance, including content strategy, keyword targeting, and off-page signals. A Technical SEO audit is a deeper, narrower technical deep-dive into server response codes, crawl budget, JavaScript rendering, and indexation controls. If you require focused engineering diagnostics, explore my{" "}
        <Link to="/TechnicalSeoPage" className="text-primary underline hover:text-primary/80 font-medium">
          dedicated Technical SEO service
        </Link>
        .
      </span>
}, {
  id: "item-7",
  question: "Does an SEO audit include backlink analysis?",
  answer: "Yes. I evaluate your inbound link profile to examine referring domain quality, anchor text distribution, toxic link risks, and identify historical link equity or potential authority gaps compared to competitors."
}, {
  id: "item-8",
  question: "Does an SEO audit include content analysis?",
  answer: "Yes. I review your primary landing pages, category structure, and informational assets to assess search intent matching, duplicate or thin content risks, keyword cannibalisation, and topical coverage gaps."
}, {
  id: "item-9",
  question: "Can you audit an ecommerce website?",
  answer: "Yes. I regularly audit e-commerce platforms across Shopify, WooCommerce, Magento, and custom headless setups, focusing on faceted navigation filtering, duplicate URL parameters, category hierarchies, product schema, and crawl efficiency."
}, {
  id: "item-10",
  question: "What happens after the SEO audit?",
  answer: "You receive the complete written diagnostic report, a clear action matrix prioritized by business impact versus implementation effort, and a video walkthrough. We also hold a 1-on-1 review call to walk through the findings and answer any questions."
}, {
  id: "item-11",
  question: "Can you help implement the recommendations?",
  answer: "Yes. Every recommendation includes precise technical and content specifications ready for your in-house developers and writers to execute, or you can retain me to assist directly with implementation guidance and post-launch QA verification."
}, {
  id: "item-12",
  question: "How much does an SEO audit cost?",
  answer: "Pricing is scoped on a bespoke basis depending on your website's size, architecture, CMS, and specific requirements. I provide a transparent, upfront quote after an initial review of your site and goals."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const [openItem, setOpenItem] = useState<string>("item-1");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="faqauditdetails" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 right-1/4 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 size-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-4xl">
        <div className="text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/20 bg-muted text-primary">
            <HelpCircle className="size-3.5 text-primary" />
            <span>SEO Audit Clarity</span>
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight font-default text-foreground mb-4">
            SEO Audit Questions & Answers
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Clear, straightforward details on how I inspect your site, deliver recommendations, and guide your search growth.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="mb-12">
          <Accordion type="single" collapsible value={openItem} onValueChange={val => setOpenItem(val)} className="space-y-4">
            {faqItems.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i}>
                <AccordionItem value={item.id} className="bg-card border border-border rounded-xl px-5 py-1 transition-all duration-200 hover:border-primary/30 shadow-xs">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold font-default text-foreground hover:no-underline hover:text-primary transition-colors py-4 text-left">
                    <span className="flex items-center gap-3">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-primary text-xs font-bold font-mono">
                        {i + 1 < 10 ? `0${i + 1}` : i + 1}
                      </span>
                      {item.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed font-default pt-1 pb-5 pl-10 pr-2">
                    <p>{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="bg-muted/60 border border-border rounded-2xl p-6 sm:p-8 text-center flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="text-left space-y-1 max-w-xl">
            <h3 className="text-lg font-semibold text-foreground font-default flex items-center gap-2">
              <MessageSquare className="size-5 text-primary shrink-0" />
              Have a specific question about your website?
            </h3>
            <p className="text-sm text-muted-foreground font-default">
              Get in touch directly to discuss your technical architecture, e-commerce challenges, or international SEO needs.
            </p>
          </div>

          <div className="shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}